import { User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import ClubBadge from "../../ClubBadge";
import { getClubBadge } from "../../../data/clubBadges";
import {
  fetchSyndicatePots, saveSyndicatePots, fetchCompetitionPlayers,
  AdminCompetition, AdminSyndicatePot, AdminSyndicatePotInput, SyndicatePotKind, SyndicatePotStatType,
  CompetitionPlayer,
} from "../../../api/admin";
import { card, primaryBtn, secondaryBtn, smallBtn, dangerBtn, inputStyle, selectStyle, panelHeading } from "../../../styles";

interface Props {
  user: User;
  comp: AdminCompetition;
  /** Rendered inline inside the Syndicate mode card on the Game Modes tab, rather than as its
   * own page — drops the redundant "Syndicate Pots" heading and card background, since the
   * enclosing mode card already provides both. */
  embedded?: boolean;
}

const STAT_TYPES: { key: SyndicatePotStatType; label: string }[] = [
  { key: "GOALS", label: "Goals" },
  { key: "ASSISTS", label: "Assists" },
  { key: "YELLOW_CARDS", label: "Yellow cards" },
  { key: "RED_CARDS", label: "Red cards" },
];

// Local editable draft — `id` absent means "not yet saved". `_key` is a stable
// client-side identity for React lists (new pots have no id to key on).
interface PotDraft {
  _key: string;
  id?: string;
  label: string;
  kind: SyndicatePotKind;
  winPoints: string;
  drawPoints: string;
  clubIds: string[];
  statType: SyndicatePotStatType;
  pointsPer: string;
  excludedPlayerIds: string[];
  excludedPlayerLabels: Record<string, string>;
}

let nextKey = 0;
const freshKey = () => `new-${Date.now()}-${nextKey++}`;

function draftFromPot(p: AdminSyndicatePot): PotDraft {
  return {
    _key: p.id,
    id: p.id,
    label: p.label,
    kind: p.kind,
    winPoints: p.winPoints != null ? String(p.winPoints) : "",
    drawPoints: p.drawPoints != null ? String(p.drawPoints) : "",
    clubIds: p.clubs.map((c) => c.id),
    statType: p.statType ?? "GOALS",
    pointsPer: p.pointsPer != null ? String(p.pointsPer) : "",
    excludedPlayerIds: p.excludedPlayerIds,
    excludedPlayerLabels: Object.fromEntries((p.excludedPlayers ?? []).map((pl) => [pl.id, `${pl.name} (${pl.club})`])),
  };
}

export default function SyndicateTab({ user, comp, embedded = false }: Props) {
  const [pots, setPots] = useState<PotDraft[]>([]);
  const [deletePotIds, setDeletePotIds] = useState<string[]>([]);
  const [frozen, setFrozen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgIsError, setMsgIsError] = useState(false);

  const load = () => {
    setLoading(true);
    fetchSyndicatePots(user, comp.id)
      .then((res) => {
        setFrozen(res.frozen);
        setPots(res.pots.map(draftFromPot));
      })
      .catch(() => setMsg("Failed to load pots"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [comp.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addPot = (kind: SyndicatePotKind) => {
    setPots((prev) => [...prev, {
      _key: freshKey(),
      label: kind === "CLUB" ? "New club pot" : "New player pot",
      kind,
      winPoints: kind === "CLUB" ? "3" : "",
      drawPoints: kind === "CLUB" ? "1" : "",
      clubIds: [],
      statType: "GOALS",
      pointsPer: kind === "PLAYER" ? "1" : "",
      excludedPlayerIds: [],
      excludedPlayerLabels: {},
    }]);
  };

  const removePot = (key: string) => {
    setPots((prev) => {
      const target = prev.find((p) => p._key === key);
      if (target?.id) setDeletePotIds((ids) => [...ids, target.id!]);
      return prev.filter((p) => p._key !== key);
    });
  };

  const move = (index: number, dir: -1 | 1) => {
    setPots((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const updatePot = (key: string, patch: Partial<PotDraft>) => {
    setPots((prev) => prev.map((p) => (p._key === key ? { ...p, ...patch } : p)));
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg("");
    try {
      const payload: AdminSyndicatePotInput[] = pots.map((p) => ({
        id: p.id,
        label: p.label.trim(),
        kind: p.kind,
        ...(p.kind === "CLUB"
          ? { winPoints: parseFloat(p.winPoints) || 0, drawPoints: parseFloat(p.drawPoints) || 0, clubIds: p.clubIds }
          : { statType: p.statType, pointsPer: parseFloat(p.pointsPer) || 0, excludedPlayerIds: p.excludedPlayerIds }),
      }));
      const result = await saveSyndicatePots(user, comp.id, payload, deletePotIds.length > 0 ? deletePotIds : undefined);
      setDeletePotIds([]);
      setMsgIsError(false);
      const notes: string[] = [];
      if (result.invalidatedPickCount > 0) notes.push(`${result.invalidatedPickCount} member pick(s) now need re-picking`);
      if (result.deletedPickCount > 0) notes.push(`${result.deletedPickCount} member pick(s) were removed with the deleted pot(s)`);
      setMsg(notes.length > 0 ? `Saved — ${notes.join("; ")}` : "Saved");
      load();
    } catch {
      setMsgIsError(true);
      setMsg("Failed to save — check for a frozen field or invalid value");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ color: "var(--color-text-muted)" }}>Loading…</p>;

  const header = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
      <div>
        {!embedded && <h2 style={{ ...panelHeading, margin: "0 0 0.25rem" }}>Syndicate Pots</h2>}
        <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
          Each pot is one pick: a club (scored on wins/draws) or a player (scored per goal/assist/card).
          Keep clubs within a pot close in strength — the best player pick should be worth roughly one strong club pick.
          Excluding only the top player just moves everyone to the second best; bar the top 2–3 for real spread.
        </p>
        {frozen && (
          <p style={{ margin: "0.5rem 0 0", fontSize: "0.8rem", color: "var(--color-warning)", fontWeight: 600 }}>
            A Syndicate league on this competition has already started — winPoints, drawPoints, statType, pointsPer, club membership, exclusions, and adding/deleting pots are locked. Only label and order can still change.
          </p>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {msg && <span style={{ fontSize: "0.82rem", color: msgIsError ? "var(--color-danger)" : "var(--color-success)" }}>{msg}</span>}
        <button style={primaryBtn} onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );

  return (
    <div>
      {embedded ? header : <div style={{ ...card, marginBottom: "0.75rem" }}>{header}</div>}

      <div style={{ display: "flex", gap: 8, marginBottom: "0.75rem", marginTop: embedded ? "0.75rem" : 0 }}>
        <button style={secondaryBtn} onClick={() => addPot("CLUB")} disabled={frozen}>+ Club pot</button>
        <button style={secondaryBtn} onClick={() => addPot("PLAYER")} disabled={frozen}>+ Player pot</button>
      </div>

      {pots.length === 0 && (
        <div style={{ ...card, color: "var(--color-text-muted)", padding: 16, fontSize: "0.9rem" }}>
          No pots configured yet — this competition needs at least one pot before a Syndicate league can be created.
        </div>
      )}

      {pots.map((pot, i) => (
        <PotCard
          key={pot._key}
          user={user}
          comp={comp}
          pot={pot}
          frozen={frozen}
          isFirst={i === 0}
          isLast={i === pots.length - 1}
          onChange={(patch) => updatePot(pot._key, patch)}
          onMove={(dir) => move(i, dir)}
          onRemove={() => removePot(pot._key)}
        />
      ))}
    </div>
  );
}

// ─── PotCard ────────────────────────────────────────────────────────────────

interface PotCardProps {
  user: User;
  comp: AdminCompetition;
  pot: PotDraft;
  frozen: boolean;
  isFirst: boolean;
  isLast: boolean;
  onChange: (patch: Partial<PotDraft>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}

function PotCard({ user, comp, pot, frozen, isFirst, isLast, onChange, onMove, onRemove }: PotCardProps) {
  const scoringLocked = frozen;

  return (
    <div style={{ ...card, marginBottom: 12, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <button style={smallBtn} onClick={() => onMove(-1)} disabled={isFirst} title="Move up">↑</button>
          <button style={smallBtn} onClick={() => onMove(1)} disabled={isLast} title="Move down">↓</button>
        </div>
        <input
          style={{ ...inputStyle, flex: 1, minWidth: 160, fontWeight: 600 }}
          value={pot.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Pot name"
        />
        <span style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--color-text-dim)", border: "1px solid var(--color-border-subtle)", borderRadius: 4, padding: "0.2rem 0.5rem" }}>
          {pot.kind}
        </span>
        <button style={dangerBtn} onClick={onRemove} disabled={frozen} title={frozen ? "Pots are frozen" : "Delete pot"}>Delete</button>
      </div>

      {pot.kind === "CLUB" ? (
        <ClubPotFields comp={comp} pot={pot} scoringLocked={scoringLocked} onChange={onChange} />
      ) : (
        <PlayerPotFields user={user} comp={comp} pot={pot} scoringLocked={scoringLocked} onChange={onChange} />
      )}
    </div>
  );
}

// ─── ClubPotFields ──────────────────────────────────────────────────────────

function ClubPotFields({ comp, pot, scoringLocked, onChange }: { comp: AdminCompetition; pot: PotDraft; scoringLocked: boolean; onChange: (patch: Partial<PotDraft>) => void }) {
  const toggleClub = (clubId: string) => {
    if (scoringLocked) return;
    const has = pot.clubIds.includes(clubId);
    onChange({ clubIds: has ? pot.clubIds.filter((id) => id !== clubId) : [...pot.clubIds, clubId] });
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}>
          Win pts
          <input type="number" step="0.01" style={{ ...inputStyle, width: 80 }} value={pot.winPoints}
            onChange={(e) => onChange({ winPoints: e.target.value })} disabled={scoringLocked} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}>
          Draw pts
          <input type="number" step="0.01" style={{ ...inputStyle, width: 80 }} value={pot.drawPoints}
            onChange={(e) => onChange({ drawPoints: e.target.value })} disabled={scoringLocked} />
        </label>
      </div>
      <p style={{ margin: "0 0 6px", fontSize: "0.78rem", color: "var(--color-text-dim)" }}>
        Eligible clubs ({pot.clubIds.length} selected){scoringLocked ? " — locked" : ""}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 6 }}>
        {comp.clubs.map((club) => {
          const selected = pot.clubIds.includes(club.id);
          return (
            <div
              key={club.id}
              onClick={() => toggleClub(club.id)}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 6,
                border: `1.5px solid ${selected ? "var(--color-accent)" : "var(--color-border)"}`,
                background: selected ? "var(--color-slot-filled-bg)" : "var(--color-surface)",
                cursor: scoringLocked ? "default" : "pointer", opacity: scoringLocked && !selected ? 0.5 : 1,
                fontSize: "0.8rem",
              }}
            >
              <ClubBadge config={getClubBadge(club.name)} size={16} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{club.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── PlayerPotFields — exclusion picker ────────────────────────────────────

function PlayerPotFields({ user, comp, pot, scoringLocked, onChange }: { user: User; comp: AdminCompetition; pot: PotDraft; scoringLocked: boolean; onChange: (patch: Partial<PotDraft>) => void }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<CompetitionPlayer[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (scoringLocked || search.trim().length < 2) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCompetitionPlayers(user, comp.id, { search, active: true })
        .then((res) => setResults(res.players.filter((p) => !pot.excludedPlayerIds.includes(p.playerId))))
        .catch(() => setResults([]));
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, scoringLocked]);

  const addExclusion = (player: CompetitionPlayer) => {
    onChange({
      excludedPlayerIds: [...pot.excludedPlayerIds, player.playerId],
      excludedPlayerLabels: { ...pot.excludedPlayerLabels, [player.playerId]: `${player.name} (${player.club.name})` },
    });
    setSearch("");
    setResults([]);
  };

  const removeExclusion = (playerId: string) => {
    if (scoringLocked) return;
    onChange({ excludedPlayerIds: pot.excludedPlayerIds.filter((id) => id !== playerId) });
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}>
          Stat
          <select style={selectStyle} value={pot.statType} onChange={(e) => onChange({ statType: e.target.value as SyndicatePotStatType })} disabled={scoringLocked}>
            {STAT_TYPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}>
          Points per
          <input type="number" step="0.01" style={{ ...inputStyle, width: 80 }} value={pot.pointsPer}
            onChange={(e) => onChange({ pointsPer: e.target.value })} disabled={scoringLocked} />
        </label>
      </div>

      <p style={{ margin: "0 0 6px", fontSize: "0.78rem", color: "var(--color-text-dim)" }}>
        Excluded players ({pot.excludedPlayerIds.length}){scoringLocked ? " — locked" : ""}
      </p>
      {pot.excludedPlayerIds.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {pot.excludedPlayerIds.map((pid) => (
            <span key={pid} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--color-surface-alt)", borderRadius: 999, padding: "3px 8px 3px 10px", fontSize: "0.78rem" }}>
              {pot.excludedPlayerLabels[pid] ?? pid}
              {!scoringLocked && (
                <button onClick={() => removeExclusion(pid)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-danger)", fontSize: "0.85rem", lineHeight: 1, padding: 0 }}>×</button>
              )}
            </span>
          ))}
        </div>
      )}
      {!scoringLocked && (
        <div style={{ position: "relative", maxWidth: 320 }}>
          <input
            style={inputStyle}
            placeholder="Search players to exclude…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {results.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 6, zIndex: 10, maxHeight: 200, overflowY: "auto" }}>
              {results.map((p) => (
                <div key={p.playerId} onClick={() => addExclusion(p)} style={{ padding: "0.4rem 0.75rem", cursor: "pointer", fontSize: "0.85rem" }}>
                  {p.name} — {p.club.name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
